'use client';

// npm library imports
import { Elements } from '@stripe/react-stripe-js';
import {
    Appearance,
    loadStripe,
    StripeElementsOptions,
} from '@stripe/stripe-js';
import { JSX } from 'react';

// Make sure to call loadStripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
// This is your publishable API key.
const stripePromise = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!,
);

/**
 * Renders a container for Stripe elements.
 *
 * @param props - The component props.
 * @param props.stripePaymentIntent - The Stripe payment intent client secret.
 * @param props.children - Child elements of the container.
 * @returns The rendered container component.
 *
 * @example
 * ```tsx
 * <StripeCardElement
 *   stripePaymentIntent={paymentIntent}
 *>
 *    <ChildComponents />
 * </StripeCardElement>
 * ```
 */
const StripeCardElement = ({
    stripePaymentIntent,
    children,
}: {
    stripePaymentIntent: string;
    children: JSX.Element;
}) => {
    const appearance: Appearance = {
        theme: 'stripe',
        variables: {
            colorPrimary: '#b74f0d',
            colorBackground: '#ffffff',
            colorText: '#191510',
            colorDanger: '#b00020',
        },
    };

    const options: StripeElementsOptions = {
        clientSecret: stripePaymentIntent,
        appearance,
    };

    return stripePaymentIntent !== '' ? (
        <Elements options={options} stripe={stripePromise}>
            {children}
        </Elements>
    ) : (
        <p>Crikey! No Payment Intent</p>
    );
};

export default StripeCardElement;
